// "Răspuns" section: answer files (several, each with a viewer), who closed the task and when.
import React, { useRef, useState } from 'react';
import { api } from '../api';
import type { FileMeta } from '../types';
import { Field, Modal, fmtBytes, fmtDateTime, useToast } from '../ui';
import { Icon } from '../ui/Icon';

/** "Intrare …" sections belong to incoming documents, "Ieșire …" to outgoing ones; other sections to both. */
export function sectionDirection(section?: string | null): 'IN' | 'OUT' | null {
  const n = (section || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
  if (n.startsWith('intrare')) return 'IN';
  if (n.startsWith('iesire')) return 'OUT';
  return null;
}

export interface ResponseFile { id: string; name: string; mime?: string; size?: number; uploadedByName?: string; createdAt?: string }
const fixTs = (iso?: string | null) => (iso ? iso.replace(/([+-]\d{2})$/, '$1:00') : iso);

export function FileViewerModal({ file, onClose }: { file: ResponseFile; onClose: () => void }) {
  const [failed, setFailed] = useState(false);
  const isImage = (file.mime || '').startsWith('image/');
  return (
    <Modal title={file.name} icon="eye" size="xl" onClose={onClose}
      footer={<><a className="btn" href={api.fileUrl(file.id, 'preview')} target="_blank" rel="noreferrer"><Icon name="arrowRight" />Deschide în fereastră nouă</a><a className="btn" href={api.fileUrl(file.id, 'download')}><Icon name="download" />Descarcă</a><span className="grow" /><button className="btn" onClick={onClose}>Închide</button></>}>
      <div className="viewer-box">
        {failed ? <div className="nofile"><Icon name="file" size={36} /><p>Previzualizarea nu este disponibilă pentru acest tip de fișier.</p></div>
          : isImage ? <img src={api.fileUrl(file.id, 'raw')} alt={file.name} onError={() => setFailed(true)} />
          : <iframe title={file.name} src={api.fileUrl(file.id, 'preview')} />}
      </div>
    </Modal>
  );
}

/** List of answer files: eye opens the viewer; when editable, files can be added (several at once) and removed. */
/** {@code editable}: files can be removed; {@code addable} (default = editable): the drop zone for adding is shown. */
export function ResponseFiles({ files, onChange, editable, addable, onView }: { files: ResponseFile[]; onChange?: (f: ResponseFile[]) => void; editable?: boolean; addable?: boolean; onView?: (f: ResponseFile) => void }) {
  const canAdd = addable ?? editable;
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(0);
  const [over, setOver] = useState(false);
  const [viewing, setViewing] = useState<ResponseFile | null>(null);
  const add = async (list: FileList | File[]) => {
    const arr = Array.from(list);
    if (!arr.length || !onChange) return;
    setBusy(arr.length);
    const added: ResponseFile[] = [];
    for (const f of arr) {
      try { const m = await api.upload<FileMeta>(f); added.push({ id: m.id, name: m.name, mime: m.mime, size: m.size }); }
      catch (e: any) { toast(`${f.name}: ${e.message}`, 'error'); }
    }
    setBusy(0);
    if (added.length) onChange([...files, ...added]);
  };
  const view = (f: ResponseFile) => (onView ? onView(f) : setViewing(f));
  return (
    <div className="stack" style={{ gap: 6 }}>
      {files.length === 0 && !canAdd && <span className="muted">Niciun fișier de răspuns</span>}
      {files.map(f => (
        <div key={f.id} className="resp-file">
          <Icon name="file" size={16} />
          <button type="button" className="resp-name" onClick={() => view(f)} title="Deschide în vizualizator">{f.name}</button>
          <span className="muted small">{fmtBytes(f.size)}</span>
          <span className="grow" />
          <button type="button" className="icon-btn" title="Vizualizează" onClick={() => view(f)}><Icon name="eye" size={16} /></button>
          <a className="icon-btn" title="Descarcă" href={api.fileUrl(f.id, 'download')}><Icon name="download" size={16} /></a>
          {editable && <button type="button" className="icon-btn" title="Elimină" onClick={() => onChange?.(files.filter(x => x.id !== f.id))}><Icon name="x" size={15} /></button>}
        </div>
      ))}
      {canAdd && (
        <div className={'dropzone sm ' + (over ? 'over' : '')} onClick={() => input.current?.click()}
          onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
          onDrop={e => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); }}>
          <input ref={input} type="file" multiple hidden onChange={e => { if (e.target.files) add(e.target.files); e.currentTarget.value = ''; }} />
          {busy ? <span>Se încarcă {busy} {busy === 1 ? 'fișier' : 'fișiere'}…</span> : <span><Icon name="upload" /> Adăugați fișiere de răspuns (unul sau mai multe) — trageți-le aici sau apăsați</span>}
        </div>
      )}
      {viewing && <FileViewerModal file={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** The whole "Răspuns" block: files + Emitent răspuns + Data răspuns (the last two are set when the task is closed). */
export function ResponseSection({ files, onFilesChange, editable, resolvedByName, resolvedAt, onView, compact }: {
  files: ResponseFile[]; onFilesChange?: (f: ResponseFile[]) => void; editable?: boolean; resolvedByName?: string | null; resolvedAt?: string | null; onView?: (f: ResponseFile) => void; compact?: boolean;
}) {
  if (compact) {
    return (
      <>
        <dl className="kv">
          <dt>Emitent răspuns</dt><dd>{resolvedByName || <span className="muted">— (se completează la rezolvare)</span>}</dd>
          <dt>Data răspuns</dt><dd>{resolvedAt ? fmtDateTime(fixTs(resolvedAt)) : <span className="muted">—</span>}</dd>
          <dt>Răspuns</dt><dd>{files.length ? `${files.length} ${files.length === 1 ? 'fișier' : 'fișiere'}` : <span className="muted">—</span>}</dd>
        </dl>
        <div style={{ marginTop: 8 }}><ResponseFiles files={files} onChange={onFilesChange} editable={editable} onView={onView} /></div>
      </>
    );
  }
  return (
    <div className="form-grid">
      <Field label="Răspuns" className="span2" hint="Fișierele cu răspunsul; ochiul le deschide în vizualizator"><ResponseFiles files={files} onChange={onFilesChange} editable={editable} onView={onView} /></Field>
      <Field label="Emitent răspuns" hint="Utilizatorul care a închis sarcina"><input className="input" readOnly tabIndex={-1} value={resolvedByName || ''} placeholder="Se completează la rezolvare" style={{ background: '#eef1f5' }} /></Field>
      <Field label="Data răspuns" hint="Data la care a fost închisă sarcina"><input className="input" readOnly tabIndex={-1} value={resolvedAt ? fmtDateTime(fixTs(resolvedAt)) : ''} placeholder="Se completează la rezolvare" style={{ background: '#eef1f5' }} /></Field>
    </div>
  );
}
